import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Filter, Plus, Split } from 'lucide-react'
import { defaultFilter } from 'cmdk'
import { TYPED_FILTER_ITEM_PREFIX, TypedFilterItems, useTypedFilter } from './TypedFilterItems'
import { useFilterLabels } from './useFilterLabels'
import type { QuickFilter } from './useQuickFilters'
import type { FilterTargetGroup } from '@/store/ProjectFilterStore'
import type { FilterFieldKey, TaskFilter } from '@/lib/types/filter'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { IssueCommandInput } from '@/components/app/combo-box/IssueCommandInput'
import { Button } from '@/components/ui/button'
import { FILTER_FIELD_DEFINITIONS, FILTER_FIELD_ORDER } from '@/lib/filters'
import { useWorkspaceMode } from '@/hooks/useWorkspaceMode'
import { cn } from '@/lib/utils'

interface FilterPropertyPickerProps {
  /** Filter store key (project or space id), for typed filters. */
  projectId: string
  scope?: 'project' | 'space'
  /** Properties typed input may not set here (hidden or pinned fields). */
  lockedFields?: Array<FilterFieldKey>
  activeFilters: Array<TaskFilter>
  onSelectField: (fieldKey: FilterFieldKey) => void
  hasActiveFilters: boolean
  hiddenFields?: Array<FilterFieldKey>
  /** One-click filters shown above the properties (design 9a). */
  quickFilters?: Array<QuickFilter>
  onQuickFilter?: (qf: QuickFilter) => void
  /** The OR group new filters go into (design 9b); undefined = no groups. */
  targetGroup?: FilterTargetGroup
  /** Offer "Combine with OR" (only once there's something to combine). */
  onCombineWithOr?: () => void
  /** Show a "+ Add filter" text trigger instead of the icon. */
  triggerLabel?: string
  /** Open straight away (a just-started OR group); closing calls onDismiss. */
  autoOpen?: boolean
  onDismiss?: () => void
}

/**
 * The Filter menu: quick filters first, then properties, with a search field
 * across both (design 9a).
 */
export function FilterPropertyPicker({
  projectId,
  scope = 'project',
  lockedFields,
  activeFilters,
  onSelectField,
  hasActiveFilters,
  hiddenFields,
  quickFilters = [],
  onQuickFilter,
  targetGroup,
  onCombineWithOr,
  triggerLabel,
  autoOpen = false,
  onDismiss,
}: FilterPropertyPickerProps) {
  const { t } = useTranslation('tasks')
  const [open, setOpen] = useState(autoOpen)
  const [search, setSearch] = useState('')
  const { isPersonal } = useWorkspaceMode()
  const { fieldLabel } = useFilterLabels()
  const typed = useTypedFilter(search, { projectId, scope, lockedFields, targetGroup })

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) {
      setSearch('')
      onDismiss?.()
    }
  }

  // One chip per property per group, so only this group's properties are taken.
  const activeFieldKeys = new Set(
    activeFilters
      .filter((f) => targetGroup !== 'new' && f.group === targetGroup)
      .map((f) => f.fieldKey),
  )
  const hiddenSet = new Set(hiddenFields ?? [])
  const availableFields = FILTER_FIELD_ORDER.filter(
    (key) =>
      !activeFieldKeys.has(key) &&
      !hiddenSet.has(key) &&
      // Everything is yours in a personal workspace.
      !(isPersonal && (key === 'assignee' || key === 'createdBy')),
  )
  const visibleQuickFilters = quickFilters.filter((qf) => !hiddenSet.has(qf.fieldKey))
  const activeCount = activeFilters.filter((f) => f.values.length > 0).length

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {triggerLabel ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <Plus className="size-3.5" />
            {triggerLabel}
          </Button>
        ) : hasActiveFilters ? (
          <Button
            variant="ghost"
            size="icon"
            className="size-7 text-muted-foreground hover:text-foreground"
            aria-label={t('filters.menu.add')}
          >
            <Plus className="size-3.5" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 text-xs text-muted-foreground hover:text-foreground phone:h-10 phone:px-2.5"
          >
            <Filter className="size-3.5 phone:size-5" />
            <span className="phone:sr-only">{t('filters.menu.button')}</span>
            {activeCount > 0 && (
              <span className="rounded-full bg-brand px-1.5 text-[10px] font-semibold leading-4 text-brand-foreground">
                {activeCount}
              </span>
            )}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command
          filter={(value, query, keywords) =>
            value.startsWith(TYPED_FILTER_ITEM_PREFIX) ? 1 : defaultFilter(value, query, keywords)
          }
        >
          <IssueCommandInput
            placeholder={t('filters.menu.search')}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>{t('filters.menu.empty')}</CommandEmpty>
            {/* Typed filters (design 9b): status:progress @me due:<fri */}
            {typed.parsed && (
              <TypedFilterItems
                parsed={typed.parsed}
                heading={t('filters.typed.heading')}
                onApply={(filters) => {
                  typed.apply(filters)
                  handleOpenChange(false)
                }}
              />
            )}
            {visibleQuickFilters.length > 0 && onQuickFilter && (
              <>
                <CommandGroup heading={t('filters.quick.heading')}>
                  {visibleQuickFilters.map((qf) => {
                    const Icon = qf.icon
                    return (
                      <CommandItem
                        key={qf.id}
                        value={`quick ${qf.label}`}
                        onSelect={() => {
                          onQuickFilter(qf)
                          handleOpenChange(false)
                        }}
                        className="gap-2 text-sm"
                      >
                        <Icon className={cn('size-4', qf.id === 'overdue' ? 'text-brand' : 'text-muted-foreground')} />
                        <span className="flex-1">{qf.label}</span>
                        {qf.active && <Check className="size-3.5 text-foreground" />}
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            {onCombineWithOr && !typed.isTyped && (
              <>
                <CommandGroup>
                  <CommandItem
                    value={`group ${t('filters.groups.combine')}`}
                    onSelect={() => {
                      onCombineWithOr()
                      handleOpenChange(false)
                    }}
                    className="gap-2 text-sm"
                  >
                    <Split className="size-4 text-muted-foreground" />
                    <span className="flex-1">{t('filters.groups.combine')}</span>
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup heading={t('filters.menu.properties')}>
              {availableFields.map((key) => {
                const fieldDef = FILTER_FIELD_DEFINITIONS[key]
                if (!fieldDef) return null
                const Icon = fieldDef.icon
                return (
                  <CommandItem
                    key={key}
                    value={`property ${fieldLabel(key, 'menu')}`}
                    onSelect={() => {
                      onSelectField(key)
                      handleOpenChange(false)
                    }}
                    className="gap-2 text-sm"
                  >
                    <Icon className="size-4 text-muted-foreground" />
                    <span>{fieldLabel(key, 'menu')}</span>
                  </CommandItem>
                )
              })}
              {availableFields.length === 0 && (
                <div className="px-3 py-3 text-center text-xs text-muted-foreground">
                  {t('filters.menu.allInUse')}
                </div>
              )}
            </CommandGroup>
          </CommandList>
          {!typed.isTyped && (
            <div className="border-t px-3 py-1.5 text-[11px] text-muted-foreground">
              {t('filters.typed.hint', { example: t('filters.typed.example') })}
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  )
}
